/**
 * Bernard Miniatures — mega menu.
 *
 * Hover-intent opening, keyboard navigation, and an A–Z index rail that
 * responds to click as well as hover. Panels are position: fixed and anchored
 * to the bottom of the header, which is measured here rather than assumed.
 */

const OPEN_DELAY = 90;
const CLOSE_DELAY = 220;

class BmMegaMenu extends HTMLElement {
  #openTimer = null;
  #closeTimer = null;
  #current = null;
  #frame = null;
  /** Viewport top of the containing block fixed panels resolve against. */
  #origin = { top: 0, left: 0 };

  connectedCallback() {
    this.openDelay = Number(this.dataset.openDelay) || OPEN_DELAY;
    this.closeDelay = Number(this.dataset.closeDelay) || CLOSE_DELAY;

    this.items = [...this.querySelectorAll('.bm-mm__item--has-panel')];
    this.links = [...this.querySelectorAll('.bm-mm__top > .bm-mm__item > .bm-mm__link, .bm-mm__top > .bm-mm__item > .bm-mm__trigger > .bm-mm__link')];
    this.scrim = this.querySelector('[data-bm-scrim]');

    this.#measure();
    this.#bindItems();
    this.#bindIndexes();
    this.#setupOverflow();

    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('focusout', this.#onFocusOut);
    this.scrim?.addEventListener('pointerenter', () => this.closeNow());

    this.resizeObserver = new ResizeObserver(() => {
      this.#measure();
      this.#reflow();
    });
    const header = this.closest('#header-component') || this.closest('header') || document.body;
    this.resizeObserver.observe(header);
    if (this.list) this.resizeObserver.observe(this.list);

    this.onScroll = () => this.#scheduleMeasure();
    window.addEventListener('scroll', this.onScroll, { passive: true });

    // Driven by a timer rather than the frame loop: ResizeObserver only
    // delivers before a paint, so a background tab would never re-flow.
    this.onResize = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.#measure();
        this.#reflow();
      }, 120);
    };
    window.addEventListener('resize', this.onResize, { passive: true });
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    clearTimeout(this.resizeTimer);
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    cancelAnimationFrame(this.#frame);
  }

  /* ---------------------------------------------------------------- layout */

  #scheduleMeasure() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#measure());
  }

  /**
   * Locate the origin that `position: fixed` actually resolves against.
   *
   * Horizon puts `contain: layout` on the header, which makes it the containing
   * block for fixed descendants — so viewport coordinates land in the wrong
   * place. Rather than hard-code that, park a panel at 0 and read back where it
   * lands: that is the origin, whatever produced it.
   *
   * @param {HTMLElement} panel - A laid-out panel to probe with.
   * @returns {{top: number, left: number}} Origin in viewport coordinates.
   */
  #probe(panel) {
    this.style.setProperty('--bm-mm-top', '0px');
    this.style.setProperty('--bm-mm-left', '0px');
    const rect = panel.getBoundingClientRect();
    return { top: rect.top, left: rect.left };
  }

  #measure() {
    const header = this.closest('#header-component') || this.closest('header');
    const bottom = header ? header.getBoundingClientRect().bottom : this.getBoundingClientRect().bottom;

    // Only a visible panel can be probed. While every panel is closed there is
    // nothing to position, so the last known origin stays good enough.
    const panel = [...this.querySelectorAll('[data-bm-panel]')].find((el) => !el.hidden);
    if (panel) this.#origin = this.#probe(panel);

    this.style.setProperty('--bm-mm-top', `${Math.max(0, Math.round(bottom - this.#origin.top))}px`);
    this.style.setProperty('--bm-mm-left', `${Math.round(-this.#origin.left)}px`);
    this.style.setProperty('--bm-mm-scrim-height', `${Math.round(window.innerHeight - bottom)}px`);
  }

  /* ------------------------------------------------------------- open/close */

  #bindItems() {
    this.items.forEach((item) => {
      const panel = item.querySelector('[data-bm-panel]');
      const triggers = [...item.querySelectorAll('[data-bm-trigger]')];
      if (!panel) return;

      item.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        this.#scheduleOpen(item);
      });

      item.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'touch') return;
        this.#scheduleClose();
      });

      triggers.forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          if (item === this.#current) {
            this.closeNow();
          } else {
            this.open(item);
          }
        });
      });
    });
  }

  #scheduleOpen(item) {
    clearTimeout(this.#closeTimer);
    clearTimeout(this.#openTimer);
    // A panel is already open: swap immediately, the menu is in "browse" mode.
    const delay = this.#current ? 0 : this.openDelay;
    this.#openTimer = setTimeout(() => this.open(item), delay);
  }

  #scheduleClose() {
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => this.closeNow(), this.closeDelay);
  }

  open(item) {
    if (!item || item === this.#current) return;
    if (this.#current) this.#hide(this.#current);

    const panel = item.querySelector('[data-bm-panel]');
    if (!panel) return;

    // Un-hide first: #measure has to read a laid-out panel to work out where
    // fixed positioning actually resolves against.
    panel.hidden = false;
    this.#measure();
    // Force a reflow so the entrance transition runs from its start state.
    void panel.offsetHeight;

    item.classList.add('is-open');
    item.querySelectorAll('[aria-expanded]').forEach((el) => el.setAttribute('aria-expanded', 'true'));

    if (this.scrim) this.scrim.hidden = false;
    this.classList.add('is-active');
    this.#current = item;

    this.#syncIndex(panel);
  }

  closeNow() {
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    if (this.#current) this.#hide(this.#current);
    this.#current = null;
    this.classList.remove('is-active');
    if (this.scrim) {
      const scrim = this.scrim;
      setTimeout(() => {
        if (!this.classList.contains('is-active')) scrim.hidden = true;
      }, 260);
    }
  }

  #hide(item) {
    const panel = item.querySelector('[data-bm-panel]');
    item.classList.remove('is-open');
    item.querySelectorAll('[aria-expanded]').forEach((el) => el.setAttribute('aria-expanded', 'false'));
    if (!panel) return;
    // Wait for the exit transition before removing it from the layout.
    setTimeout(() => {
      if (!item.classList.contains('is-open')) panel.hidden = true;
    }, 300);
  }

  /* -------------------------------------------------------------- keyboard */

  #onKeydown = (event) => {
    if (event.key === 'Escape' && this.#current) {
      const item = this.#current;
      this.closeNow();
      item.querySelector('.bm-mm__link, [data-bm-trigger]')?.focus();
      return;
    }

    const link = event.target.closest('.bm-mm__top > .bm-mm__item .bm-mm__link, .bm-mm__caret');
    if (!link) return;
    const item = link.closest('.bm-mm__item');

    if (event.key === 'ArrowDown' && item?.classList.contains('bm-mm__item--has-panel')) {
      event.preventDefault();
      this.open(item);
      // A timer, not a frame: a background tab never runs rAF.
      setTimeout(() => {
        item.querySelector('[data-bm-panel] a, [data-bm-panel] button:not(:disabled)')?.focus();
      }, 0);
      return;
    }

    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      const all = [...this.querySelectorAll('.bm-mm__top > .bm-mm__item')];
      const index = all.indexOf(item);
      if (index === -1) return;
      event.preventDefault();
      const next = all[(index + (event.key === 'ArrowRight' ? 1 : all.length - 1)) % all.length];
      next?.querySelector('.bm-mm__link')?.focus();
    }
  };

  #onFocusOut = (event) => {
    if (!this.#current) return;
    if (event.relatedTarget && this.contains(event.relatedTarget)) return;
    this.closeNow();
  };

  /* ---------------------------------------------------------- bar overflow */

  #setupOverflow() {
    this.list = this.querySelector('.bm-mm__top');
    this.moreItem = this.querySelector('[data-bm-more]');
    this.moreList = this.querySelector('[data-bm-more-list]');
    if (!this.list || !this.moreItem) return;
    this.baseItems = [...this.list.children].filter((el) => el !== this.moreItem);
    this.#reflow();
  }

  /**
   * How much room the bar actually has.
   *
   * Measuring the list itself is circular: the header column is sized to its
   * content, so showing every item widens the very box we are measuring
   * against. Instead the nav is collapsed to zero, the header is measured at
   * its minimum, and whatever is left over is what the bar can use.
   */
  /**
   * What an element actually needs, rather than the box the grid handed it.
   * The header's right-hand column is right-aligned inside a stretchy track, so
   * its box width absorbs whatever slack is going and would make the bar look
   * far more cramped than it is.
   */
  static #contentWidth(element) {
    const rect = element.getBoundingClientRect();
    const children = [...element.children].filter((child) => child.getBoundingClientRect().width > 0);
    if (!children.length) return rect.width;

    const style = getComputedStyle(element);
    const gap = parseFloat(style.columnGap) || 0;
    const padding = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
    const content =
      children.reduce((sum, child) => sum + child.getBoundingClientRect().width, 0) +
      gap * (children.length - 1) +
      padding;

    return Math.min(rect.width, content);
  }

  #availableWidth() {
    const nav = this.querySelector('.bm-mm__nav');
    if (!nav) return 0;

    const navLeft = nav.getBoundingClientRect().left;
    const container = this.closest('.header__columns') || this.closest('#header-component') || this.closest('header');
    if (!container) return this.list.clientWidth - 8;

    // Right edge of the usable area, minus whatever has to sit to the right of
    // the bar (localization, search, cart…).
    let boundary = Math.min(container.getBoundingClientRect().right, document.documentElement.clientWidth);
    const node = this.closest('.header__column') || this;
    for (let sibling = node.nextElementSibling; sibling; sibling = sibling.nextElementSibling) {
      boundary -= BmMegaMenu.#contentWidth(sibling);
    }

    // 16px of slack keeps sub-pixel layout from toggling an item in and out.
    return Math.max(0, boundary - navLeft - 16);
  }

  #reflow() {
    if (!this.list || !this.moreItem || !this.baseItems) return;

    if (this.reflowing) return;
    this.reflowing = true;

    const gap = parseFloat(getComputedStyle(this.list).columnGap) || 0;

    // Measure from a clean slate. Hidden items report a width of zero, and —
    // more importantly — the neighbours the bar competes with only shrink back
    // to their minimum once the bar is asking for its full width again.
    this.baseItems.forEach((item) => {
      item.hidden = false;
    });
    this.moreItem.hidden = true;

    const widths = this.baseItems.map((item) => item.offsetWidth);
    const available = this.#availableWidth();
    if (available <= 0) {
      this.reflowing = false;
      return;
    }
    let total = widths.reduce((sum, w) => sum + w, 0) + gap * (this.baseItems.length - 1);
    if (total <= available) {
      this.moreList.replaceChildren();
      this.reflowing = false;
      return;
    }

    this.moreItem.hidden = false;
    const reserved = this.moreItem.offsetWidth + gap;
    const overflowed = [];

    for (let i = this.baseItems.length - 1; i >= 0 && total + reserved > available; i -= 1) {
      const item = this.baseItems[i];
      // Never push the first two rubrics into the bucket: a nav that collapses
      // to "Plus" alone helps nobody.
      if (i < 2) break;
      item.hidden = true;
      total -= widths[i] + gap;
      overflowed.unshift(item);
    }

    if (!overflowed.length) {
      this.moreItem.hidden = true;
      this.reflowing = false;
      return;
    }

    if (this.#current && overflowed.includes(this.#current)) this.closeNow();

    this.moreList.replaceChildren(
      ...overflowed.map((item) => {
        const source = item.querySelector('a.bm-mm__link');
        const row = document.createElement(source ? 'a' : 'span');
        row.className = 'bm-mm__row';
        if (source) row.href = source.getAttribute('href');
        const label = document.createElement('span');
        label.className = 'bm-mm__row-label';
        label.textContent = item.querySelector('.bm-mm__label')?.textContent?.trim() ?? '';
        row.appendChild(label);
        return row;
      })
    );

    this.reflowing = false;
  }

  /* ----------------------------------------------------------- index rails */

  #bindIndexes() {
    this.querySelectorAll('[data-bm-index]').forEach((index) => {
      const scroller = index.querySelector('[data-bm-index-scroll]');
      const letters = [...index.querySelectorAll('.bm-mm__letter')];
      if (!scroller) return;

      const goTo = (letter) => {
        const group = scroller.querySelector(`[data-bm-letter-group="${letter}"]`);
        if (!group) return;
        // Measured rather than derived from offsetTop, which depends on which
        // ancestor happens to be positioned.
        const delta = group.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        scroller.scrollTo({ top: scroller.scrollTop + delta, behavior: reduced ? 'auto' : 'smooth' });
      };

      const mark = (button) => {
        letters.forEach((other) => other.classList.toggle('is-current', other === button));
      };

      letters.forEach((button) => {
        if (button.disabled) return;
        const jump = () => {
          // Marked straight away: the last letters cannot always reach the top
          // of the list, so the observer alone would highlight the wrong one.
          mark(button);
          goTo(button.dataset.bmLetter);
        };
        button.addEventListener('click', jump);
        button.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'touch') return;
          jump();
        });
      });

      // Keep the rail in sync with whatever group is at the top of the list.
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const letter = entry.target.dataset.bmLetterGroup;
            letters.forEach((button) => {
              button.classList.toggle('is-current', button.dataset.bmLetter === letter);
            });
          });
        },
        { root: scroller, rootMargin: '0px 0px -80% 0px', threshold: 0 }
      );

      scroller.querySelectorAll('[data-bm-letter-group]').forEach((group) => observer.observe(group));
      index.bmObserver = observer;
    });
  }

  #syncIndex(panel) {
    const scroller = panel.querySelector('[data-bm-index-scroll]');
    if (scroller) scroller.scrollTop = 0;
  }
}

if (!customElements.get('bm-mega-menu')) {
  customElements.define('bm-mega-menu', BmMegaMenu);
}
